<template>
	<div class="b-color-picker-wrapper" ref="wrapper">
		<b-input-group>
			<b-input
				ref="input"
				:id="`color-picker-button-${_uid}`"
				block
				@focus="show = true"
				:disabled="disabled"
				v-model="value"
			>
			</b-input>
			<b-input-group-append>
				<b-button @click="$refs.input.focus()" :disabled="disabled">
					<div class="checkerboard">
						<div class="color-display-solid" :style="{ backgroundColor: rgbColor }"></div>
						<div class="color-display-transparent" :style="transparentStyle"></div>
					</div>
				</b-button>
			</b-input-group-append>
		</b-input-group>

		<b-popover
			:target="`color-picker-button-${_uid}`"
			:show.sync="show"
			placement="bottom"
			triggers=""
		>
			<div
				class="sl-selector"
				:style="slStyle"
				@mousedown="startDraggingSL"
				ref="slSelector"
			>
				<div class="sl-handle" :style="slHandleStyle"></div>
			</div>
			<div class="hue-slider-container">
				<input
					type="range"
					class="hue-range"
					min="0"
					max="360"
					v-model="hue"
					@input="updateHue"
				/>
			</div>
			<div class="alpha-slider-container">
				<div class="alpha-track">
					<input
						type="range"
						class="alpha-range"
						min="0"
						max="100"
						v-model="alpha"
						:style="alphaTrackStyle"
						@input="updateAlpha"
					/>
				</div>
			</div>
		</b-popover>
	</div>
</template>

<script>
export default {
	name: 'BColorPicker',
	props: {
		value: {
			type: String,
			default: '#000000'
		},
		disabled: Boolean,
	},
	data() {
		return {
			show: false,
			hue: 0,
			x: 100,
			y: 50,
			alpha: 100,
			buttonId: null,
		}
	},
	computed: {
		rgbColor() {
			const [r, g, b] = this.hxyToRgb(this.hue, this.x, this.y)
			return this.alpha < 1 ? `rgba(${r}, ${g}, ${b}, 0)` : `rgb(${r}, ${g}, ${b})`;
		},
		hexValue() {
			const [r, g, b] = this.hxyToRgb(this.hue, this.x, this.y)
			const hex = '#' + [r, g, b].map(x => {
				const hex = x.toString(16)
				return hex.length === 1 ? '0' + hex : hex
			}).join('').toUpperCase()
			return this.alpha < 100 ? hex + this.alphaToHex(this.alpha) : hex
		},
		slStyle() {
			return {
				background: `
					linear-gradient(to right, hsl(${this.hue}, 0%, 100%) 0%, hsl(${this.hue}, 100%, 50%) 100%),
					linear-gradient(to top, hsl(${this.hue}, 100%, 0%) 0%, hsl(${this.hue}, 100%, 100%) 100%)
				`,
			}
		},
		slHandleStyle() {
			return {
				left: this.x + '%',
				top: (100 - this.y) + '%'
			}
		},
		alphaTrackStyle() {
			const [r, g, b] = this.hxyToRgb(this.hue, this.x, this.y)
			return {
				background: `linear-gradient(to right,
					rgba(${r}, ${g}, ${b}, 0),
					rgba(${r}, ${g}, ${b}, 1))`
			}
		},
		transparentStyle() {
			const [r, g, b] = this.hxyToRgb(this.hue, this.x, this.y)
			const alphaValue = this.alpha / 100
			return {
				backgroundColor: `rgba(${r}, ${g}, ${b}, ${alphaValue})`
			}
		}
	},
	methods: {
		startDraggingSL(e) {
			e.preventDefault();
			const rect = this.$refs.slSelector.getBoundingClientRect()
			const handleSL = (e) => this.updateSL(e, rect)
			const stopDragging = () => {
				document.removeEventListener('mousemove', handleSL)
				document.removeEventListener('mouseup', stopDragging)
			}
			document.addEventListener('mousemove', handleSL)
			document.addEventListener('mouseup', stopDragging)
			this.updateSL(e, rect)
		},
		updateSL(e, rect) {
			const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
			const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height))
			this.x = Math.round((x / rect.width) * 100)
			this.y = Math.round(100 - (y / rect.height) * 100)
			this.$emit('input', this.hexValue)
		},
		updateHue(e) {
			this.hue = parseInt(e.target.value)
			this.$emit('input', this.hexValue)
		},
		updateAlpha(e) {
			this.alpha = parseInt(e.target.value)
			this.$emit('input', this.hexValue)
		},
		updateFromInput(e) {
			const input = e.target.value.trim();
			let r, g, b, a = 1;

			// HEX format (#rrggbb or #rrggbbaa)
			if (/^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/.test(input)) {
				const cleanHex = input.replace('#', '');
				r = parseInt(cleanHex.substr(0, 2), 16);
				g = parseInt(cleanHex.substr(2, 2), 16);
				b = parseInt(cleanHex.substr(4, 2), 16);
				if (cleanHex.length === 8)
					a = parseInt(cleanHex.substr(6, 2), 16) / 255;

			// rgb() or rgba() format
			} else if (/^rgba?\s*\(([^)]+)\)$/i.test(input)) {
				const parts = input
					.match(/^rgba?\s*\(([^)]+)\)$/i)[1]
					.split(',')
					.map(v => v.trim());

				if (parts.length >= 3) {
					r = parseInt(parts[0]);
					g = parseInt(parts[1]);
					b = parseInt(parts[2]);
					if (parts.length === 4) a = parseFloat(parts[3]);
				}
			} else {
				// not a valid color
				return;
			}

			if (
				[r, g, b].some(v => isNaN(v) || v < 0 || v > 255) ||
				isNaN(a) || a < 0 || a > 1
			) return;

			// convert RGBA → internal hue/sat/lightness/alpha
			[this.hue, this.x, this.y] = this.rgbToHxy(r, g, b);
			this.alpha = Math.round(a * 100);

			this.$emit('input', this.hexValue);
		},
		hxyToRgb(h, x, y) {
			const s = x / 100;
			const l = y / 100 * (1 - s / 2);

			const c = (1 - Math.abs(2 * l - 1)) * s;
			const hp = h / 60;
			const xcol = c * (1 - Math.abs((hp % 2) - 1));
			let [r, g, b] = [0, 0, 0];

			if (0 <= hp && hp < 1) [r, g, b] = [c, xcol, 0];
			else if (1 <= hp && hp < 2) [r, g, b] = [xcol, c, 0];
			else if (2 <= hp && hp < 3) [r, g, b] = [0, c, xcol];
			else if (3 <= hp && hp < 4) [r, g, b] = [0, xcol, c];
			else if (4 <= hp && hp < 5) [r, g, b] = [xcol, 0, c];
			else if (5 <= hp && hp < 6) [r, g, b] = [c, 0, xcol];

			const m = l - c / 2;
			return [
				Math.round((r + m) * 255),
				Math.round((g + m) * 255),
				Math.round((b + m) * 255),
			];
		},
		rgbToHxy(r, g, b) {
			r /= 255; g /= 255; b /= 255;
			const max = Math.max(r, g, b);
			const min = Math.min(r, g, b);
			const d = max - min;

			let h = 0;
			if (d !== 0) {
				if (max === r) h = ((g - b) / d) % 6;
				else if (max === g) h = (b - r) / d + 2;
				else h = (r - g) / d + 4;
				h *= 60;
				if (h < 0) h += 360;
			}

			const l = (max + min) / 2;
			const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

			const x = s * 100;      // horizontal position
			const y = l * 100 / (1 - s / 2)

			return [ h, x, y ];
		},
		alphaToHex(alpha) {
			const hex = Math.round((alpha / 100) * 255).toString(16)
			return (hex.length === 1 ? '0' : '') + hex
		},
	},
	watch: {
		value(newVal) {
			if(newVal && newVal !== this.hexValue) {
				this.updateFromInput({ target: { value: newVal } })
			}
		}
	},
	mounted() {
		if(this.value) this.updateFromInput({ target: { value: this.value } })
		this._onClickOutside = (e) => {
			if(this.show && this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)
				&& !e.target.closest('.popover'))
				this.show = false;
		};
		document.addEventListener('mousedown', this._onClickOutside);
	},
	beforeDestroy() {
		document.removeEventListener('mousedown', this._onClickOutside);
	},
}
</script>

<style lang="scss">
@import '@/plugins/variable.scss';

.b-color-picker-wrapper {
	position: relative;

	.checkerboard {
		width: 50px;
		height: 1em;
		position: relative;
		background-image:
			linear-gradient(45deg, #888 25%, transparent 25%),
			linear-gradient(-45deg, #888 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, #888 75%),
			linear-gradient(-45deg, transparent 75%, #888 75%);
		background-size: 10px 10px;
		background-position: 0 0, 0 5px, 5px -5px, -5px 0px;

		.color-display-solid {
			position: absolute;
			left: 0;
			top: 0;
			width: 50%;
			height: 100%
		}

		.color-display-transparent {
			position: absolute;
			right: 0;
			top: 0;
			width: 50%;
			height: 100%;
			background-color: #fff;
		}
	}
}

.sl-selector {
	background-blend-mode: multiply;
	width: 100%;
	height: 150px;
	position: relative;
	margin-bottom: 12px;
	border-radius: 2px;
	cursor: crosshair;
}

.sl-handle {
	position: absolute;
	width: 12px;
	height: 12px;
	border: 2px solid white;
	border-radius: 50%;
	box-shadow: 0 0 0 1px #000;
	transform: translate(-50%, -50%);
	pointer-events: none;
}

.hue-slider-container {
	margin-bottom: 12px;
}

.hue-range {
	width: 100%;
	height: 12px;
	-webkit-appearance: none;
	appearance: none;
	background: linear-gradient(to right,
		hsl(0, 100%, 50%),
		hsl(60, 100%, 50%),
		hsl(120, 100%, 50%),
		hsl(180, 100%, 50%),
		hsl(240, 100%, 50%),
		hsl(300, 100%, 50%),
		hsl(360, 100%, 50%));
	border-radius: 2px;
	cursor: pointer;
	outline: none;
}

.hue-range::-webkit-slider-thumb, .alpha-range::-webkit-slider-thumb {
	width: 6px;
	height: 20px;
	border: 2px solid white;
	border-radius: 2px;
	-webkit-appearance: none;
	appearance: none;
	cursor: pointer;
	box-shadow: 0 0 0 1px #000;
}

.hue-range::-moz-range-thumb, .alpha-range::-moz-range-thumb {
	width: 6px;
	height: 20px;
	border: 2px solid white;
	border-radius: 2px;
	background: #fff;
	cursor: pointer;
	box-shadow: 0 0 0 1px #000;
}

.alpha-slider-container {
	margin-bottom: 12px;
	position: relative;
}

.alpha-track {
	width: 100%;
	height: 12px;
	border-radius: 2px;
	background-image:
		linear-gradient(45deg, #888 25%, transparent 25%),
		linear-gradient(-45deg, #888 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, #888 75%),
		linear-gradient(-45deg, transparent 75%, #888 75%);
	background-size: 10px 10px;
	background-position: 0 0, 0 5px, 5px -5px, -5px 0px;
	position: relative;
}

.alpha-range {
	width: 100%;
	height: 12px;
	-webkit-appearance: none;
	appearance: none;
	border-radius: 2px;
	cursor: pointer;
	outline: none;
	position: relative;
	z-index: 2;
	top: -3px;
}

.hex-input {
	width: 100%;
	padding: 6px 8px;
	border: 1px solid #ccc;
	border-radius: 2px;
	font-size: 13px;
	font-family: monospace;
	text-transform: uppercase;
	box-sizing: border-box;
}

.hex-input:focus {
	outline: none;
	border-color: #007bff;
	box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
}

input[type="range"] {
	user-select: none;
	-webkit-user-select: none;
}
</style>
